import React, { useCallback, useState } from 'react'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { pick } from 'ramda'
import { routeManager } from 'core/utils/routes'
import useReactRouter from 'use-react-router'
import DocumentMeta from 'core/components/DocumentMeta'
import DataKeys from 'k8s/DataKeys'
import { ArrayElement } from 'core/actions/Action'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import { listApplicationCredentials, deleteApplicationCredential } from './actions'
import ListContainer from 'core/containers/ListContainer'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useGlobalParams from 'core/hooks/useGlobalParams'
import { applicationCredentialsSelector } from './selectors'
import InferActionParams from 'core/actions/InferActionParams'
import { useAppSelector } from 'app/store'
import CreateApplicationCredentialModal from './CreateApplicationCredentialModal'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { whitelistedRoles } from 'app/plugins/account/components/userManagement/roles/selectors'
import Tooltip from 'core/elements/tooltip'
import Text from 'core/elements/Text'
import { APPLICATION_CREDENTIALS_ROLE_LABEL } from './constant'
import CustomDeleteDialog from 'core/components/CustomDeleteDialog'

type ModelDataKey = DataKeys.ApplicationCredentials
type SelectorModel = ArrayElement<ReturnType<typeof applicationCredentialsSelector>>
type ActionParams = InferActionParams<typeof listApplicationCredentials>
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'name',
  orderDirection: 'asc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'ApplicationCredentials',
  listTablePrefs,
)

const searchTargets = ['name', 'id', 'description']

const applicationCredentialColumns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
    CellComponent: createGridLinkCell({
      routeToFn: ({ id }) =>
        routeManager.routes.openstack.applicationCredentialDetails.path({ id }),
    }),
  },
  {
    key: 'id',
    label: 'UUID',
    display: false,
  },
  {
    key: 'roleName',
    label: 'Role',
    render: (val, row) => {
      const roles = row.roles
        ?.filter((role) => whitelistedRoles.includes(role.name))
        .map((role) => APPLICATION_CREDENTIALS_ROLE_LABEL[role.name])
      const rolesString = roles.join(', ')

      return (
        <Tooltip message={rolesString}>
          <Text variant="body2">
            {roles.length > 1 ? `${roles[0]} + ${roles.length - 1}` : rolesString}
          </Text>
        </Tooltip>
      )
    },
  },
  {
    key: 'formattedExpiration',
    label: 'Expiration',
  },
  {
    key: 'description',
    label: 'Description',
  },
  {
    key: 'restricted',
    label: 'Restricted',
    render: (val) => (val ? 'True' : 'False'),
  },
  {
    key: 'accessRules',
    label: 'Access Rules',
    render: (val, row) => {
      const summary = row.accessRulesSummary
      if (
        !summary ||
        (summary.servicesCount === 0 && summary.pathsCount === 0 && summary.methodsCount === 0)
      ) {
        return 'None'
      }
      return (
        <span>
          services: <b>{summary.servicesCount}</b>&nbsp;&nbsp;&nbsp;&nbsp;paths:{' '}
          <b>{summary.pathsCount}</b>&nbsp;&nbsp;&nbsp;&nbsp;methods: <b>{summary.methodsCount}</b>
        </span>
      )
    },
  },
]

export default function ApplicationCredentialsListPage() {
  const { history } = useReactRouter()
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { message, loading, reload } = useListAction(listApplicationCredentials, {
    params,
  })
  const data = useAppSelector(applicationCredentialsSelector)
  const [selectedCredential, setSelectedCredential] = useState(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = [
    {
      label: 'View Details',
      icon: 'eye',
      handleClick: (credential) => {
        history.push(
          routeManager.routes.openstack.applicationCredentialDetails.path({ id: credential.id }),
        )
      },
      refreshAfterSuccess: false,
      hideIfDisabled: false,
    },
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleClick: (credential) => {
        setSelectedCredential(credential)
        setShowDeleteDialog(true)
      },
      refreshAfterSuccess: true,
      hideIfDisabled: false,
    },
  ]

  const DeleteApplicationCredentialDialog = useCallback(
    (props: any) => (
      <CustomDeleteDialog
        {...props}
        deleteAction={deleteApplicationCredential}
        title="Delete Application Credential(s)"
        resourceLabel="Application Credentials"
      />
    ),
    [],
  )

  return (
    <>
      <DocumentMeta title="Application Credentials" />
      <CreateApplicationCredentialModal
        addRoute={routeManager.routes.openstack.createApplicationCredential}
        onSuccess={reload}
      />
      {showDeleteDialog && selectedCredential && (
        <DeleteApplicationCredentialDialog
          rows={[selectedCredential]}
          onClose={() => {
            setShowDeleteDialog(false)
            setSelectedCredential(null)
          }}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.ApplicationCredentials}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={data}
        columns={applicationCredentialColumns}
        addUrl={routeManager.routes.openstack.createApplicationCredential.path()}
        addText="Create Application Credential"
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteApplicationCredential}
        DeleteDialogComponent={DeleteApplicationCredentialDialog}
        rowMenuItems={rowMenuItems}
        isReadOnly={!isAdmin()}
        multiSelection
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
